import {
  CloseOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { Button, Col, Drawer, DrawerProps, Modal, Row, Typography } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import PageTitle from '../../components/PageTitle';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import styles from './EdtEmailTemplateDrawer.module.css';

interface EditEmailTemplateDrawerProps extends DrawerProps {
  loading?: boolean;
  templateName?: string;
  onSave?: () => void;
  onDelete?: () => void;
  deleteDisabled?: boolean;
}

const EditEmailTemplateDrawer = ({
  loading,
  children,
  templateName,
  onSave,
  onDelete,
  deleteDisabled,
  ...props
}: EditEmailTemplateDrawerProps) => {
  const { t } = useTranslation();

  const { tenant } = useTypedParams({ tenant: String });
  const navigate = useTypedNavigate();
  const [searchParams] = useSearchParams();

  const onClose = () =>
    navigate({
      pathname: '/:tenant/settings/emailTemplates/',
      params: { tenant },
      search: searchParams,
    });
  return (
    <>
      <Drawer
        open
        size="large"
        className={styles.drawer}
        closable={false}
        destroyOnClose
        maskClosable
        onClose={onClose}
        title={
          <PageTitle
            title={templateName || '...'}
            level={2}
            extra={[
              <Button key="close" type="text" onClick={onClose} shape="circle">
                <CloseOutlined />
              </Button>,
            ]}
          />
        }
        footer={
          <Row gutter={[16, 16]}>
            <Col>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={loading}
                onClick={() => onSave?.()}
              >
                {' '}
                <Trans>Salvar</Trans>
              </Button>
            </Col>
            <Col>
              <Button loading={loading} onClick={onClose}>
                <Trans>Fechar</Trans>
              </Button>
            </Col>
            <Col flex={1} />
            <Col>
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={loading}
                disabled={deleteDisabled}
                onClick={() => {
                  return new Promise((resolve) => {
                    const modal = Modal.confirm({});
                    modal.update({
                      title: 'Atenção!',
                      icon: <ExclamationCircleOutlined />,
                      content: (
                        <Typography.Text>
                          <Trans>Ao confirmar, o modelo de email</Trans>{' '}
                          <strong>{templateName}</strong>{' '}
                          <Trans>
                            será excluído e não poderá ser recuperado. Você tem certeza que deseja
                            excluir?
                          </Trans>
                        </Typography.Text>
                      ),
                      cancelButtonProps: {
                        onClick: () => {
                          modal.destroy();
                          resolve(true);
                        },
                      },
                      okText: t('Sim, excluir modelo'),
                      okButtonProps: {
                        onClick: () => {
                          onDelete?.();
                          modal.destroy();
                          resolve(false);
                        },
                      },
                      closable: true,
                      maskClosable: true,
                    });
                  });
                }}
              >
                {' '}
                <Trans>Deletar</Trans>
              </Button>
            </Col>
          </Row>
        }
        {...props}
      >
        {children}
      </Drawer>
    </>
  );
};

export default EditEmailTemplateDrawer;
